import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Download, TrendingUp, Users, Calendar, FileText, AlertTriangle, CheckCircle, XCircle, Filter, RefreshCw, Church, Home } from "lucide-react";
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { format } from "date-fns";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['#10b981', '#ef4444', '#f59e0b', '#8b5cf6', '#3b82f6'];

export default function ReportsPage({ user }) {
  const [reportData, setReportData] = useState(null);
  const [insights, setInsights] = useState(null);
  const [filteredReport, setFilteredReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [leaders, setLeaders] = useState([]);
  const [disciples, setDisciples] = useState([]);
  
  // Filters
  const [filters, setFilters] = useState({
    leader_id: "",
    disciple_id: "",
    attendance_type: "",
    start_date: "",
    end_date: ""
  });

  const [activeView, setActiveView] = useState("overview"); // overview, homecell, church, filtered
  const [availableDisciples, setAvailableDisciples] = useState([]);

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    // Filter disciples when leader is selected
    if (filters.leader_id && filters.leader_id !== "all") {
      const filteredDisciples = disciples.filter(d => d.homecell_leader_id === filters.leader_id);
      setAvailableDisciples(filteredDisciples);
    } else {
      setAvailableDisciples(disciples);
    }
  }, [filters.leader_id, disciples]);

  const fetchInitialData = async () => {
    try {
      const token = localStorage.getItem("token");
      const [reportsRes, insightsRes, leadersRes, disciplesRes] = await Promise.all([
        axios.get(`${API}/attendance/by-leader`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/attendance/insights`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/users`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/disciples`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setReportData(reportsRes.data);
      setInsights(insightsRes.data);
      setLeaders(leadersRes.data.filter(u => u.role === 'homecell_leader'));
      setDisciples(disciplesRes.data);
    } catch (error) {
      toast.error("Failed to fetch reports");
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = async () => {
    try {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      
      const response = await axios.get(`${API}/attendance/filtered-report?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setFilteredReport(response.data);
      setActiveView("filtered");
      toast.success("Filters applied successfully");
    } catch (error) {
      toast.error("Failed to apply filters");
    }
  };

  const resetFilters = () => {
    setFilters({
      leader_id: "",
      disciple_id: "",
      attendance_type: "",
      start_date: "",
      end_date: ""
    });
    setFilteredReport(null);
    setActiveView("overview");
  };

  const downloadReport = () => {
    const report = {
      generated_at: new Date().toISOString(),
      generated_by: user.name,
      active_view: activeView,
      filters_applied: activeView === "filtered" ? filters : null,
      insights: insights,
      data: activeView === "filtered" ? filteredReport : reportData
    };

    const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `attendance-report-${activeView}-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Report downloaded");
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin h-12 w-12 border-4 border-purple-600 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-600">Loading reports...</p>
        </div>
      </div>
    );
  }

  const leaderStats = reportData ? Object.values(reportData) : [];

  return (
    <div className="p-8">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            <span className="gradient-text">Attendance Reports</span>
          </h1>
          <p className="text-gray-600">Comprehensive analytics with advanced filtering</p>
        </div>
        <Button 
          data-testid="download-report-button" 
          onClick={downloadReport} 
          className="bg-gradient-to-r from-purple-600 to-indigo-600"
        >
          <Download className="mr-2 h-4 w-4" /> Download Report
        </Button>
      </div>

      {/* View Tabs */}
      <div className="card mb-6">
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => setActiveView("overview")}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              activeView === "overview" ? "bg-purple-600 text-white" : "bg-gray-100 hover:bg-gray-200"
            }`}
            data-testid="view-overview"
          >
            <TrendingUp className="inline h-4 w-4 mr-2" />
            Overview
          </button>
          <button
            onClick={() => setActiveView("homecell")}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              activeView === "homecell" ? "bg-purple-600 text-white" : "bg-gray-100 hover:bg-gray-200"
            }`}
            data-testid="view-homecell"
          >
            <Home className="inline h-4 w-4 mr-2" />
            Homecell Meetings
          </button>
          <button
            onClick={() => setActiveView("church")}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              activeView === "church" ? "bg-purple-600 text-white" : "bg-gray-100 hover:bg-gray-200"
            }`}
            data-testid="view-church"
          >
            <Church className="inline h-4 w-4 mr-2" />
            Church Services
          </button>
          <button
            onClick={() => setActiveView("filtered")}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              activeView === "filtered" ? "bg-purple-600 text-white" : "bg-gray-100 hover:bg-gray-200"
            }`}
            data-testid="view-filtered"
          >
            <Filter className="inline h-4 w-4 mr-2" />
            Custom Filter
          </button>
        </div>
      </div>

      {/* Filters Panel - Show when filtered view is active */}
      {activeView === "filtered" && (
        <div className="card mb-6 border-l-4 border-purple-600">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold flex items-center gap-2">
              <Filter className="h-5 w-5" />
              Advanced Filters
            </h3>
            <Button variant="outline" size="sm" onClick={resetFilters}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Reset
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-4">
            <div>
              <Label>Homecell Leader</Label>
              <Select value={filters.leader_id || "all"} onValueChange={(val) => setFilters({...filters, leader_id: val === "all" ? "" : val})}>
                <SelectTrigger data-testid="filter-leader">
                  <SelectValue placeholder="All Leaders" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Leaders</SelectItem>
                  {leaders.map(leader => (
                    <SelectItem key={leader.id} value={leader.id}>{leader.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Disciple</Label>
              <Select value={filters.disciple_id || "all"} onValueChange={(val) => setFilters({...filters, disciple_id: val === "all" ? "" : val})}>
                <SelectTrigger data-testid="filter-disciple">
                  <SelectValue placeholder="All Disciples" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Disciples</SelectItem>
                  {availableDisciples.map(disc => (
                    <SelectItem key={disc.id} value={disc.id}>
                      {disc.name} {disc.surname}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Attendance Type</Label>
              <Select value={filters.attendance_type || "all"} onValueChange={(val) => setFilters({...filters, attendance_type: val === "all" ? "" : val})}>
                <SelectTrigger data-testid="filter-type">
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="homecell">Homecell</SelectItem>
                  <SelectItem value="church_service">Church Service</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Start Date</Label>
              <Input
                type="date"
                value={filters.start_date}
                onChange={(e) => setFilters({...filters, start_date: e.target.value})}
                data-testid="filter-start-date"
              />
            </div>

            <div>
              <Label>End Date</Label>
              <Input
                type="date"
                value={filters.end_date}
                onChange={(e) => setFilters({...filters, end_date: e.target.value})}
                data-testid="filter-end-date"
              />
            </div>
          </div>

          <Button onClick={applyFilters} data-testid="apply-filters" className="bg-purple-600">
            Apply Filters
          </Button>
        </div>
      )}

      {/* Overview Stats */}
      {insights && (activeView === "overview" || activeView === "filtered") && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="stat-card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/80 text-sm">Overall Rate</p>
                <p className="text-3xl font-bold">{activeView === "filtered" && filteredReport ? filteredReport.summary.attendance_rate : insights.overall_attendance_rate}%</p>
              </div>
              <TrendingUp className="h-12 w-12 text-white/40" />
            </div>
          </div>

          <div className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Homecell Rate</p>
                <p className="text-3xl font-bold text-purple-600">{insights.homecell_attendance_rate}%</p>
              </div>
              <Home className="h-12 w-12 text-purple-500" />
            </div>
          </div>

          <div className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Church Rate</p>
                <p className="text-3xl font-bold text-blue-600">{insights.church_attendance_rate}%</p>
              </div>
              <Church className="h-12 w-12 text-blue-500" />
            </div>
          </div>

          <div className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Total Records</p>
                <p className="text-3xl font-bold text-gray-800">
                  {activeView === "filtered" && filteredReport ? filteredReport.summary.total_records : (insights.homecell_stats.total_records + insights.church_stats.total_records)}
                </p>
              </div>
              <FileText className="h-12 w-12 text-gray-500" />
            </div>
          </div>
        </div>
      )}

      {/* Comparison Charts - Overview */}
      {activeView === "overview" && insights && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <div className="card">
            <h3 className="text-lg font-bold mb-4">Homecell vs Church Service Comparison</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={[
                { name: 'Homecell', present: insights.homecell_stats.present, absent: insights.homecell_stats.absent },
                { name: 'Church', present: insights.church_stats.present, absent: insights.church_stats.absent }
              ]}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="present" fill="#10b981" name="Present" />
                <Bar dataKey="absent" fill="#ef4444" name="Absent" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="card">
            <h3 className="text-lg font-bold mb-4">Attendance Rate Distribution</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={[
                    { name: 'Homecell', value: insights.homecell_attendance_rate },
                    { name: 'Church', value: insights.church_attendance_rate }
                  ]}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({name, value}) => `${name}: ${value}%`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {[0, 1].map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Homecell Meetings Tab */}
      {activeView === "homecell" && reportData && (
        <div className="space-y-6 mb-8">
          {Object.entries(reportData).map(([leaderId, leaderData]) => {
            const homecellDisciples = leaderData.disciples.filter(d => 
              d.attendance_by_date && Object.values(d.attendance_by_date).some(dateRecords => 
                dateRecords.some(r => r.type === 'homecell')
              )
            );
            
            if (homecellDisciples.length === 0) return null;
            
            return (
              <div key={leaderId} className="card">
                <h3 className="text-xl font-bold mb-4 text-purple-700">{leaderData.leader_name}'s Homecell</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <p className="text-sm text-purple-600 font-medium">Total Disciples</p>
                    <p className="text-2xl font-bold text-purple-900">{homecellDisciples.length}</p>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg">
                    <p className="text-sm text-green-600 font-medium">Avg Rate</p>
                    <p className="text-2xl font-bold text-green-900">
                      {homecellDisciples.length > 0 
                        ? Math.round(homecellDisciples.reduce((sum, d) => sum + d.attendance_rate, 0) / homecellDisciples.length)
                        : 0}%
                    </p>
                  </div>
                </div>
                
                <div className="space-y-2">
                  {homecellDisciples.map((disc) => (
                    <div key={disc.disciple_id} className={`p-3 rounded-lg ${
                      disc.attendance_rate >= 75 ? 'bg-green-50' : disc.attendance_rate >= 50 ? 'bg-yellow-50' : 'bg-red-50'
                    }`}>
                      <div className="flex items-center justify-between">
                        <span className="font-semibold">{disc.disciple_name}</span>
                        <div className="flex items-center gap-4">
                          <span className="text-sm text-green-700">
                            <CheckCircle className="inline h-4 w-4" /> {disc.present_count}
                          </span>
                          <span className="text-sm text-red-700">
                            <XCircle className="inline h-4 w-4" /> {disc.absent_count}
                          </span>
                          <span className={`text-lg font-bold ${
                            disc.attendance_rate >= 75 ? 'text-green-700' : disc.attendance_rate >= 50 ? 'text-yellow-700' : 'text-red-700'
                          }`}>
                            {disc.attendance_rate}%
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Church Services Tab */}
      {activeView === "church" && reportData && (
        <div className="space-y-6 mb-8">
          {Object.entries(reportData).map(([leaderId, leaderData]) => {
            const churchDisciples = leaderData.disciples.filter(d => 
              d.attendance_by_date && Object.values(d.attendance_by_date).some(dateRecords => 
                dateRecords.some(r => r.type === 'church_service')
              )
            );
            
            if (churchDisciples.length === 0) return null;
            
            return (
              <div key={leaderId} className="card">
                <h3 className="text-xl font-bold mb-4 text-blue-700">{leaderData.leader_name}'s Disciples - Church Service</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="text-sm text-blue-600 font-medium">Total Disciples</p>
                    <p className="text-2xl font-bold text-blue-900">{churchDisciples.length}</p>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg">
                    <p className="text-sm text-green-600 font-medium">Avg Rate</p>
                    <p className="text-2xl font-bold text-green-900">
                      {churchDisciples.length > 0 
                        ? Math.round(churchDisciples.reduce((sum, d) => sum + d.attendance_rate, 0) / churchDisciples.length)
                        : 0}%
                    </p>
                  </div>
                </div>
                
                <div className="space-y-2">
                  {churchDisciples.map((disc) => (
                    <div key={disc.disciple_id} className={`p-3 rounded-lg ${
                      disc.attendance_rate >= 75 ? 'bg-green-50' : disc.attendance_rate >= 50 ? 'bg-yellow-50' : 'bg-red-50'
                    }`}>
                      <div className="flex items-center justify-between">
                        <span className="font-semibold">{disc.disciple_name}</span>
                        <div className="flex items-center gap-4">
                          <span className="text-sm text-green-700">
                            <CheckCircle className="inline h-4 w-4" /> {disc.present_count}
                          </span>
                          <span className="text-sm text-red-700">
                            <XCircle className="inline h-4 w-4" /> {disc.absent_count}
                          </span>
                          <span className={`text-lg font-bold ${
                            disc.attendance_rate >= 75 ? 'text-green-700' : disc.attendance_rate >= 50 ? 'text-yellow-700' : 'text-red-700'
                          }`}>
                            {disc.attendance_rate}%
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Filtered Report Display */}
      {activeView === "filtered" && filteredReport && (
        <div className="space-y-6 mb-8">
          <div className="card">
            <h3 className="text-lg font-bold mb-4">Filtered Results Summary</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-green-50 p-4 rounded-lg">
                <p className="text-sm text-green-600 font-medium">Present</p>
                <p className="text-2xl font-bold text-green-900">{filteredReport.summary.present}</p>
              </div>
              <div className="bg-red-50 p-4 rounded-lg">
                <p className="text-sm text-red-600 font-medium">Absent</p>
                <p className="text-2xl font-bold text-red-900">{filteredReport.summary.absent}</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg">
                <p className="text-sm text-purple-600 font-medium">Rate</p>
                <p className="text-2xl font-bold text-purple-900">{filteredReport.summary.attendance_rate}%</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-sm text-blue-600 font-medium">Disciples</p>
                <p className="text-2xl font-bold text-blue-900">{filteredReport.summary.unique_disciples}</p>
              </div>
            </div>
          </div>

          {filteredReport.timeline && filteredReport.timeline.length > 0 && (
            <div className="card">
              <h3 className="text-lg font-bold mb-4">Timeline Analysis</h3>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={filteredReport.timeline}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{fontSize: 12}} />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="present" stroke="#10b981" name="Present" strokeWidth={2} />
                  <Line type="monotone" dataKey="absent" stroke="#ef4444" name="Absent" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          <div className="card">
            <h3 className="text-lg font-bold mb-4">Disciple Performance</h3>
            <div className="space-y-2">
              {filteredReport.disciple_breakdown.map((disc) => (
                <div key={disc.disciple_id} className={`p-3 rounded-lg ${
                  disc.rate >= 75 ? 'bg-green-50' : disc.rate >= 50 ? 'bg-yellow-50' : 'bg-red-50'
                }`}>
                  <div className="flex items-center justify-between">
                    <span className="font-semibold">{disc.disciple_name}</span>
                    <div className="flex items-center gap-4">
                      <span className="text-sm text-green-700">
                        <CheckCircle className="inline h-4 w-4" /> {disc.present}
                      </span>
                      <span className="text-sm text-red-700">
                        <XCircle className="inline h-4 w-4" /> {disc.absent}
                      </span>
                      <span className={`text-lg font-bold ${
                        disc.rate >= 75 ? 'text-green-700' : disc.rate >= 50 ? 'text-yellow-700' : 'text-red-700'
                      }`}>
                        {disc.rate}%
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Insights & Suggestions */}
      {insights && insights.suggestions && insights.suggestions.length > 0 && activeView === "overview" && (
        <div className="card mb-8 border-l-4 border-orange-500">
          <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
            <AlertTriangle className="h-6 w-6 text-orange-600" />
            Key Insights & Recommendations
          </h2>
          <div className="space-y-4">
            {insights.suggestions.map((suggestion, idx) => (
              <div key={idx} className={`border-2 rounded-lg p-4 ${
                suggestion.priority === 'high' ? 'bg-red-50 border-red-200' :
                suggestion.priority === 'medium' ? 'bg-yellow-50 border-yellow-200' :
                'bg-blue-50 border-blue-200'
              }`}>
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <span className={`text-xs font-bold uppercase px-2 py-1 rounded ${
                      suggestion.priority === 'high' ? 'bg-red-200 text-red-800' :
                      suggestion.priority === 'medium' ? 'bg-yellow-200 text-yellow-800' :
                      'bg-blue-200 text-blue-800'
                    }`}>
                      {suggestion.priority} Priority
                    </span>
                    {suggestion.category && (
                      <span className="ml-2 text-xs bg-gray-200 text-gray-700 px-2 py-1 rounded">
                        {suggestion.category}
                      </span>
                    )}
                  </div>
                </div>
                <h4 className="font-bold text-lg mb-2">{suggestion.issue}</h4>
                <p className="mb-3">{suggestion.suggestion}</p>
                <div className="bg-white/60 rounded p-3">
                  <p className="font-semibold text-sm mb-2">Action Items:</p>
                  <ul className="space-y-1">
                    {suggestion.action_items.map((action, i) => (
                      <li key={i} className="text-sm flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 mt-0.5 text-green-600 flex-shrink-0" />
                        {action}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {leaderStats.length === 0 && (
        <div className="text-center py-12">
          <FileText className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No data available. Start recording attendance!</p>
        </div>
      )}
    </div>
  );
}
